import { useQueries, useQuery } from 'react-query';

import axiosClient from '../../api/axios-client';
import type {
    ChecklistTemplate,
    HousekeepingDisplaySettings,
    HousekeepingRoomStatus,
} from '../../pages/housekeeping/types/housekeeping';

/**
 * Les lectures de « Mes chambres » — lot 3 Odomo.
 *
 * ## Recopiées, pas importées
 *
 * Les hooks du web vivent dans `pages/housekeeping/api/`, que la frontière du socle
 * interdit d'atteindre (#23). Seuls les TYPES en viennent, en `import type` : ils
 * s'effacent au build.
 *
 * ## L'établissement est dans la clé
 *
 * `useApiQuery` range sous `[url, config]` : deux hôtels partageraient la même clé,
 * et une bascule servirait une seconde les chambres de l'hôtel précédent. Même
 * parade que `useConversations` — la donnée périmée est impossible par
 * construction. L'établissement réel part dans `X-Facility-Id`, posé par
 * `axios-client` depuis la sélection.
 */

const URL_MES_CHAMBRES = '/housekeeping/my-rooms';

/** Le web sonde ses listes ménage toutes les 60 s ; même rythme ici. */
const SONDAGE_MS = 60_000;

export interface OptionsMesChambres {
    facilityId: number | null;
    /** Y-m-d. Absent : aujourd'hui. Présent : un jour de planning, en lecture seule. */
    date?: string;
}

export function urlMesChambres(date?: string): string {
    return date ? `${URL_MES_CHAMBRES}?date=${date}` : URL_MES_CHAMBRES;
}

export function cleMesChambres({ facilityId, date }: OptionsMesChambres) {
    return [URL_MES_CHAMBRES, { facilityId, date: date ?? null }] as const;
}

/** Aujourd'hui bouge (la gouvernante affecte, le PMS synchronise) ; demain est un planning figé. */
export function sondageMesChambres(date?: string): number | false {
    return date ? false : SONDAGE_MS;
}

export function useMesChambres({ facilityId, date }: OptionsMesChambres) {
    return useQuery<HousekeepingRoomStatus[]>(
        cleMesChambres({ facilityId, date }),
        () => axiosClient.get(urlMesChambres(date)).then((res) => res.data),
        {
            enabled: facilityId !== null,
            refetchInterval: sondageMesChambres(date),
        },
    );
}

/** `today` et `show_tomorrow_to_agents` : lisibles avec `housekeeping.view`. */
export function useReglagesAffichageMenage(facilityId: number | null) {
    return useQuery<HousekeepingDisplaySettings>(
        ['/housekeeping/display-settings', { facilityId }],
        () => axiosClient.get('/housekeeping/display-settings').then((res) => res.data),
        { enabled: facilityId !== null, staleTime: 5 * 60 * 1000 },
    );
}

/**
 * Le détail des checklists applicables à une chambre (auto par type ∪ manuelle),
 * chargées en parallèle. Les identifiants de modèle sont uniques en base : la clé
 * n'a pas besoin de l'établissement. Triées par id, pour un rendu stable.
 */
export function useChecklistsDeLaChambre(ids: number[]) {
    const resultats = useQueries(
        ids.map((id) => ({
            queryKey: [`/housekeeping/checklists/${id}`, {}],
            queryFn: () =>
                axiosClient.get(`/housekeeping/checklists/${id}`).then((res) => res.data as ChecklistTemplate),
        })),
    );

    const templates = resultats
        .map((r) => r.data as ChecklistTemplate | undefined)
        .filter((tpl): tpl is ChecklistTemplate => Boolean(tpl))
        .sort((a, b) => a.id - b.id);

    return {
        templates,
        isLoading: resultats.some((r) => r.isLoading),
        isError: resultats.some((r) => r.isError),
        recharger: () => resultats.forEach((r) => void r.refetch()),
    };
}
